import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useSignUp, type SignUpBody} from 'api/generated';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
// import {APPLE_SVG, GOOGLE_SVG} from 'lib';
import {usePersistentStore} from 'stores';
import {
  Transitioner,
  Button,
  InputSecret,
  InputText,
  InputCheckbox,
  //  ButtonOmniAuth,
  //  TextWithLine
} from 'components';

const schema = z.object({
  email: z.string().email('Email is invalid'),
  password: z.string().min(6, 'Password must be at least 6 character(s)'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  agreement: z.boolean(),
});

interface IFormSignUpProps {}

const FormSignUp = ({}: IFormSignUpProps) => {
  const setAuthToken = usePersistentStore(s => s.setAuthToken);

  const signUp = useSignUp();
  const {
    register,
    handleSubmit,
    watch,
    formState: {errors},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: SignUpBody) => {
    const {data: res} = await signUp.mutateAsync({data});
    if (res.data) {
      setAuthToken(res.data.jwt);
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  return (
    <section className="flex flex-col flex-1 p-6 lg:px-8">
      <Transitioner>
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h1 className="text-center text-2xl/9 font-bold tracking-tight dark:text-white">Sign up an account</h1>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {/* TODO: Implement this */}
          {/* <div className="flex flex-col gap-6 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <ButtonOmniAuth svg={GOOGLE_SVG} text="Google" />
              <ButtonOmniAuth svg={APPLE_SVG} text="Apple" />
            </div>
            <TextWithLine text="Or continue with" />
          </div> */}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
            <InputText label="Email Address" type="email" autoComplete="email" autoFocus {...register('email')} error={errors.email?.message} />
            <InputSecret
              label="Password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              error={errors.password?.message}
            />
            <div className="grid grid-cols-2 gap-3">
              <InputText label="First Name" {...register('firstName')} error={errors.firstName?.message} />
              <InputText label="Last Name" {...register('lastName')} error={errors.lastName?.message} />
            </div>
            <InputCheckbox {...register('agreement')} name="agreement">
              {({id}) => {
                return (
                  <label htmlFor={id} className="block text-sm/6 text-gray-900 dark:text-white cursor-pointer">
                    I agree with the{' '}
                    <a href="https://das-ar.com/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      terms
                    </a>{' '}
                    and{' '}
                    <a href="https://das-ar.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      privacy policy
                    </a>
                  </label>
                );
              }}
            </InputCheckbox>
            <div className="mt-5">
              <Button type="submit" text="Sign up" fullWidth size="small" disabled={!watch('agreement')} loading={signUp.isPending} />
            </div>
          </form>

          <p className="mt-10 text-center text-sm/6">
            Already have an account?{' '}
            <Link to="/sign-in" className="font-semibold text-primary hover:brightness-115 transition duration-200">
              Sign in here
            </Link>
          </p>
        </div>
      </Transitioner>
    </section>
  );
};

export default FormSignUp;
